import { useCallback, useEffect, useRef, useState } from 'react'
import { useLang } from '../i18n'
import { useAuth } from '../auth'

interface NotificationItem {
  id: string
  recipient_id: string
  type: string
  payload: Record<string, unknown>
  read_at: string | null
  created_at: string | null
}

async function fetchNotifications(token: string): Promise<NotificationItem[]> {
  const resp = await fetch('/api/notifications', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!resp.ok) return []
  const data = await resp.json()
  return data.data ?? []
}

async function markRead(token: string, id: string): Promise<void> {
  await fetch(`/api/notifications/${id}/read`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  })
}

function notificationLabel(item: NotificationItem, t: (k: string) => string): string {
  switch (item.type) {
    case 'task_assigned': {
      const tasks = (item.payload.tasks as { title?: string }[] | undefined) ?? []
      if (tasks.length === 1) return t('notifTaskAssigned1').replace('{title}', tasks[0].title ?? '')
      return t('notifTaskAssignedN').replace('{n}', String(tasks.length))
    }
    case 'task_withdrawn':
      return t('notifTaskWithdrawn').replace('{title}', (item.payload.title as string) ?? '')
    case 'task_reassigned':
      return t('notifTaskReassigned').replace('{title}', (item.payload.title as string) ?? '')
    case 'task_overdue':
      return t('notifTaskOverdue').replace('{title}', (item.payload.title as string) ?? '')
    case 'noncompliant_created':
      return t('notifNoncompliantCreated')
    default:
      return t('notifGeneric')
  }
}

export function NotificationBell() {
  const { t } = useLang()
  const { accessToken } = useAuth()
  const [items, setItems] = useState<NotificationItem[]>([])
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null)

  const load = useCallback(async () => {
    if (!accessToken) return
    const data = await fetchNotifications(accessToken)
    setItems(data)
  }, [accessToken])

  useEffect(() => {
    load()
    const interval = setInterval(load, 30000)
    return () => clearInterval(interval)
  }, [load])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // When panel opens, compute fixed position from button's viewport rect
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPanelPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      })
    }
    if (!open) {
      setPanelPos(null)
    }
  }, [open])

  const unreadCount = items.filter((n) => n.read_at === null).length

  const handleItemClick = async (item: NotificationItem) => {
    if (!accessToken || item.read_at !== null) return
    await markRead(accessToken, item.id)
    setItems((prev) =>
      prev.map((n) =>
        n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n,
      ),
    )
  }

  if (!accessToken) return null

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        aria-label={t('notifications')}
        style={{
          position: 'relative',
          padding: '4px 8px',
          borderRadius: '4px',
          border: '1px solid rgba(255,255,255,0.3)',
          background: 'transparent',
          color: 'var(--nav-text)',
          cursor: 'pointer',
          fontSize: '16px',
          lineHeight: 1,
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: 'var(--danger)',
              color: 'var(--accent-contrast)',
              borderRadius: '9999px',
              fontSize: '10px',
              minWidth: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              fontWeight: 700,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed',
            top: panelPos?.top ?? 0,
            right: panelPos?.right ?? 0,
            width: '320px',
            maxHeight: '400px',
            overflowY: 'auto',
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border)',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            {t('notifications')}
            {unreadCount > 0 && (
              <span
                style={{
                  marginLeft: '8px',
                  background: 'var(--danger)',
                  color: 'var(--accent-contrast)',
                  borderRadius: '9999px',
                  fontSize: '11px',
                  padding: '1px 6px',
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>

          {items.length === 0 ? (
            <div style={{ padding: '16px', color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center' }}>
              {t('notifEmpty')}
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                  cursor: item.read_at === null ? 'pointer' : 'default',
                  background: item.read_at === null ? 'var(--info-bg)' : 'var(--bg-elevated)',
                  fontSize: '13px',
                  transition: 'background 0.1s',
                }}
              >
                <div style={{ fontWeight: item.read_at === null ? 600 : 400 }}>
                  {notificationLabel(item, t)}
                </div>
                {item.created_at && (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {new Date(item.created_at).toLocaleString()}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
